import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { Suburb } from '~/types/Location';
import type { ApiInstance } from '~/types/plugins/api';

interface ApiSuburbElectorate {
  id: number;
  name: string;
  region?: { id: number; name: string } | null;
}

interface ApiSuburb {
  id: number;
  name: string;
  postcodes: string[] | null;
  electorates?: ApiSuburbElectorate[];
}

function mapSuburb(raw: ApiSuburb): Suburb {
  return {
    id: String(raw.id),
    suburb: raw.name,
    postcodes: raw.postcodes ?? [],
    electorates: (raw.electorates ?? []).map((electorate) => electorate.name),
    region: raw.electorates?.[0]?.region?.name ?? null,
  };
}

// The `/suburbs?include=electorates.region` endpoint returns the whole ~3000-row
// unpaginated table (~300KB, multi-second on a cold connection). Suburb
// boundaries/electorates change rarely, so a day-long localStorage cache
// avoids refetching it every time the location picker opens.
// v2: entries gained `region`; a v1 cache would show no region for a day.
const CACHE_KEY = 'votewise:suburbs:v2';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface SuburbCacheEntry {
  data: Suburb[];
  timestamp: number;
}

function readCache(): Suburb[] | null {
  if (!import.meta.client) return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SuburbCacheEntry> | null;
    if (
      !parsed ||
      !Array.isArray(parsed.data) ||
      typeof parsed.timestamp !== 'number' ||
      Date.now() - parsed.timestamp > CACHE_TTL_MS
    ) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data: Suburb[]): void {
  if (!import.meta.client) return;
  try {
    const entry: SuburbCacheEntry = { data, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Storage full/unavailable/disabled — caching is a perf nicety, not a
    // requirement, so silently skip it rather than surfacing an error.
  }
}

export function useSuburbSearch(
  query: MaybeRefOrGetter<string>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const { enabled = true } = options;
  const { $apiClient } = useNuxtApp() as unknown as { $apiClient: ApiInstance };

  const cached = readCache();
  const allSuburbs = ref<Suburb[]>(cached ?? []);
  const pending = ref(false);
  const error = ref<unknown>(null);
  let hasLoaded = cached !== null;

  const fetchSuburbs = async () => {
    if (hasLoaded || pending.value) return;
    hasLoaded = true;
    pending.value = true;
    error.value = null;

    try {
      const response = await $apiClient.get<
        { data: ApiSuburb[] } | ApiSuburb[]
      >('/suburbs', { params: { include: 'electorates.region' } });
      const raw = Array.isArray(response) ? response : response.data;
      const mapped = raw.map(mapSuburb);
      allSuburbs.value = mapped;
      writeCache(mapped);
    } catch (err) {
      error.value = err;
      hasLoaded = false; // allow a retry on the next enable/mount
    } finally {
      pending.value = false;
    }
  };

  watch(
    () => toValue(enabled),
    (isEnabled) => {
      if (isEnabled) fetchSuburbs();
    },
    { immediate: true },
  );

  const suburbs = computed<Suburb[]>(() => {
    const q = toValue(query).trim().toLowerCase();
    if (!q) return allSuburbs.value;
    return allSuburbs.value.filter(
      (s) =>
        s.suburb.toLowerCase().includes(q) ||
        s.postcodes.some((postcode) => postcode.includes(q)),
    );
  });

  return { suburbs, pending: readonly(pending), error: readonly(error) };
}
